import type { PlaybookSelection } from "@repo/app-types/entities/playbook";
import type { SignalMetadataSchema } from "@repo/app-types/entities/signal";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { FieldError } from "@repo/ui/field/fieldError";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { IconChevronDown } from "@tabler/icons-react";
import type { FC } from "react";
import { DialogTrigger } from "react-aria-components";
import { DiffableField } from "../../../components/diffableField.js";
import { CopilotProposalBanner } from "../../../components/recordingAssessmentEditor/copilotProposalBanner.js";
import { ResourceSaveFooter } from "../../../components/resourceSaveFooter.js";
import { MetadataSchemaBuilder } from "./metadataSchemaBuilder.js";
import type { SignalFormFields } from "./signalFormHelpers.js";
import { SignalPlaybookScopeField } from "./signalPlaybookScopeField.js";
import {
  SignalTestResultPill,
  type SignalTestResultPillState,
} from "./signalTestResultPill.js";

export interface SignalEditorContainerProps {
  name: string;
  description: string;
  metadataSchema: SignalMetadataSchema;
  playbookScope: PlaybookSelection;
  error: string | null;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onMetadataSchemaChange: (value: SignalMetadataSchema) => void;
  onPlaybookScopeChange: (value: PlaybookSelection) => void;
  /**
   * The configuration the form is diffed against: the draft under a proposal,
   * the live definition otherwise. Absent when there is nothing to compare to,
   * as on a signal that has never been published.
   */
  diffBaseline: SignalFormFields | undefined;
  onCancel: () => void;
  onSave: () => void;
  onDelete?: () => void;
  isSaving: boolean;
  isDeleting: boolean;
  isSaveDisabled: boolean;
  hasUnsavedChanges: boolean;
  onTestSignalClick: () => void;
  onConfigureTestClick: () => void;
  isTestSignalDisabled: boolean;
  testResultPillState: SignalTestResultPillState | null;
  onViewMatches: () => void;
  /**
   * A pending copilot proposal laid over the form: the form is a read-only
   * preview and the banner offers Apply / Discard.
   */
  suggestion?: {
    isNewSignal: boolean;
    onDiscard: () => void;
    onApply: () => void;
    /** A decision is in flight; both are held until it lands. */
    isResolving: boolean;
  };
}

export const SignalEditorContainer: FC<SignalEditorContainerProps> = ({
  name,
  description,
  metadataSchema,
  playbookScope,
  error,
  onNameChange,
  onDescriptionChange,
  onMetadataSchemaChange,
  onPlaybookScopeChange,
  diffBaseline,
  onCancel,
  onSave,
  onDelete,
  isSaving,
  isDeleting,
  isSaveDisabled,
  hasUnsavedChanges,
  onTestSignalClick,
  onConfigureTestClick,
  isTestSignalDisabled,
  testResultPillState,
  onViewMatches,
  suggestion,
}) => {
  const isReadOnly = suggestion !== undefined;

  return (
    <div className="flex h-full flex-col">
      {/* Top toolbar */}
      <div className="relative z-10 flex items-center justify-end bg-card px-4 py-2 after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-6 after:h-6 after:bg-linear-to-b after:from-card after:to-transparent after:content-['']">
        <div className="flex items-center gap-2">
          {onDelete ? (
            <Button
              variant={ButtonVariant.Warning}
              onClick={onDelete}
              isDisabled={isDeleting}
              size={Size.SMALL}
            >
              Delete
            </Button>
          ) : null}
          <div className="flex items-stretch">
            <Button
              variant={ButtonVariant.Secondary}
              onClick={onTestSignalClick}
              isDisabled={isTestSignalDisabled}
              size={Size.SMALL}
              className="rounded-r-none"
            >
              Test signal
            </Button>
            <DialogTrigger>
              <Button
                variant={ButtonVariant.Secondary}
                icon={<IconChevronDown className="h-4 w-4" />}
                isDisabled={isTestSignalDisabled}
                size={Size.SMALL}
                className="-ml-px rounded-l-none px-2"
              />
              <SimpleMenuPopover placement="bottom end">
                <SimpleMenu aria-label="Test signal options">
                  <SimpleMenuItem onAction={onConfigureTestClick}>
                    Configure test…
                  </SimpleMenuItem>
                </SimpleMenu>
              </SimpleMenuPopover>
            </DialogTrigger>
          </div>
        </div>
      </div>

      {suggestion ? (
        <CopilotProposalBanner
          className="mx-0 mt-0 rounded-none border-b border-border-light"
          message={
            suggestion.isNewSignal
              ? "Showing a new signal Ask Alpharun suggests"
              : "Showing changes Ask Alpharun suggests"
          }
          discardLabel="Discard changes"
          confirmLabel="Apply changes"
          onDiscard={suggestion.onDiscard}
          onConfirm={suggestion.onApply}
          isDisabled={suggestion.isResolving}
        />
      ) : null}

      {/* Form content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto flex max-w-xl flex-col space-y-6">
          {testResultPillState ? (
            <SignalTestResultPill
              state={testResultPillState}
              onViewMatches={onViewMatches}
            />
          ) : null}

          <div>
            <div className="mb-1">
              <Label size={Size.SMALL}>Name</Label>
            </div>
            <Input
              id="signal_name"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Signal name"
              maxLength={200}
              isDisabled={isReadOnly}
            />
          </div>

          <div>
            <DiffableField
              label={<Label size={Size.SMALL}>Description</Label>}
              savedValue={diffBaseline?.description}
              value={description}
            >
              <Textarea
                id="signal_description"
                value={description}
                onChange={onDescriptionChange}
                placeholder="e.g. Customer mentioning one of our competitors"
                maxLength={5000}
                height="15rem"
                isDisabled={isReadOnly}
              />
            </DiffableField>
            <FieldDescription className="mt-1">
              A natural language definition of the conversational pattern to
              detect.
            </FieldDescription>
          </div>

          <div className="flex flex-col gap-1">
            <Label size={Size.SMALL}>Playbooks</Label>
            <SignalPlaybookScopeField
              value={playbookScope}
              onChange={onPlaybookScopeChange}
              isDisabled={isReadOnly}
            />
            <FieldDescription>
              The playbooks and voice agents whose calls this signal is detected
              in.
            </FieldDescription>
          </div>

          <div>
            <MetadataSchemaBuilder
              value={metadataSchema}
              onChange={onMetadataSchemaChange}
              isDisabled={isReadOnly}
            />
          </div>

          {error ? <FieldError>{error}</FieldError> : null}
        </div>
      </div>

      {hasUnsavedChanges && !suggestion ? (
        <ResourceSaveFooter
          onCancel={onCancel}
          onSave={onSave}
          isSaving={isSaving}
          isSaveDisabled={isSaveDisabled}
        />
      ) : null}
    </div>
  );
};
